import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Bell, Calendar, Megaphone, Cake, X, CheckCheck, Receipt, MessageSquare, ExternalLink } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

export const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(null);
  const navigate = useNavigate();

  const getIcon = (type) => {
    switch (type) {
      case 'event': return <Calendar className="w-4 h-4 text-sky-500" />;
      case 'birthday': return <Cake className="w-4 h-4 text-pink-500" />;
      case 'announcement': return <Megaphone className="w-4 h-4 text-amber-500" />;
      case 'message': return <MessageSquare className="w-4 h-4 text-emerald-500" />;
      case 'presupuesto_cuota':
      case 'presupuesto_pago':
      case 'presupuesto_confirmado':
      case 'presupuesto_recordatorio':
        return <Receipt className="w-4 h-4 text-blue-500" />;
      case 'presupuesto_info': return <Receipt className="w-4 h-4 text-purple-500" />;
      default: return <Bell className="w-4 h-4 text-emerald-500" />;
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d MMM, HH:mm", { locale: es });
    } catch {
      return '';
    }
  };

  const handleDismiss = async (id, e) => {
    e.stopPropagation();
    setDismissing(id);
    // Small delay for animation
    setTimeout(() => {
      markAsRead(id);
      setDismissing(null);
    }, 250);
  };

  const handleClearAll = async () => {
    await markAllAsRead();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center px-1 rounded-full bg-red-500 text-white text-[10px] font-bold animate-in zoom-in duration-200">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-sm">Notificaciones</h4>
            <p className="text-xs text-muted-foreground">
              {unreadCount > 0 ? `${unreadCount} pendiente${unreadCount > 1 ? 's' : ''}` : 'Todo al día'}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 text-muted-foreground hover:text-foreground"
              onClick={handleClearAll}
              data-testid="clear-all-notifications-btn"
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" /> Limpiar todo
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {notifications.length > 0 ? (
            <div>
              {notifications.slice(0, 30).map((notif) => (
                <div
                  key={notif.id}
                  className={`group relative border-b border-stone-100 last:border-0 transition-all duration-250 ease-out overflow-hidden ${
                    dismissing === notif.id
                      ? 'max-h-0 opacity-0 -translate-x-full py-0'
                      : 'max-h-40 opacity-100 translate-x-0'
                  }`}
                  data-testid={`notification-item-${notif.id}`}
                >
                  <div className="flex items-start gap-3 p-3 hover:bg-stone-50/80">
                    <div className="mt-0.5 shrink-0">{getIcon(notif.notification_type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-stone-800 leading-tight">{notif.title}</p>
                      {notif.link ? (
                        <>
                          <p className="text-xs text-stone-500 mt-0.5">{notif.message}</p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notif.id);
                              setOpen(false);
                              navigate(notif.link);
                            }}
                            className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 font-medium mt-1 hover:underline"
                            data-testid={`notif-link-${notif.id}`}
                          >
                            <ExternalLink className="w-3 h-3" />
                            {notif.link_label || 'Ir a ver mensaje'}
                          </button>
                        </>
                      ) : (
                        <p className="text-xs text-stone-500 line-clamp-2 mt-0.5">{notif.message}</p>
                      )}
                      <p className="text-[11px] text-stone-400 mt-1">{formatDate(notif.created_at)}</p>
                    </div>
                    <button
                      className="shrink-0 p-1 rounded-full opacity-0 group-hover:opacity-100 hover:bg-stone-200 transition-opacity"
                      onClick={(e) => handleDismiss(notif.id, e)}
                      data-testid={`dismiss-notification-${notif.id}`}
                    >
                      <X className="w-3.5 h-3.5 text-stone-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-10 text-center">
              <Bell className="w-8 h-8 mx-auto mb-2 text-stone-300" />
              <p className="text-sm text-stone-400">Sin notificaciones</p>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
